<script setup>
  /* global defineProps, defineEmits */
  import { computed, nextTick, shallowRef, watch } from 'vue'
  import { ElMessage } from 'element-plus'
  import ReplayQualityNotice from './ReplayQualityNotice.vue'
  import ReplayOutputCards from './ReplayOutputCards.vue'
  import { replayOutputLabel } from './replayLabels'

  const props = defineProps({
    task: { type: Object, default: null },
    allowRegenerate: Boolean,
    busy: Boolean,
  })

  const emit = defineEmits(['regenerate'])
  const retainedOutput = shallowRef(null)
  const videoRef = shallowRef(null)
  const activeKey = shallowRef('')
  const lastReadyKey = shallowRef('')
  const fallbackKey = shallowRef('')
  const pendingRestore = shallowRef(null)
  const playbackWasActive = shallowRef(false)
  const isRecovering = shallowRef(false)
  const readyKeys = new Set()

  watch(
    () => props.task?.taskId,
    () => {
      retainedOutput.value = null
      activeKey.value = ''
      lastReadyKey.value = ''
      fallbackKey.value = ''
      readyKeys.clear()
    },
    { flush: 'sync' }
  )

  const mediaKey = (output, index) => {
    const identity = output.id == null ? `${output.audioType || 'OUTPUT'}-${output.language || index}` : output.id
    return `output-${identity}|${output.videoUrl}`
  }

  const outputCards = computed(() =>
    (props.task?.outputs || [])
      .map((output, index) => ({ ...output, key: mediaKey(output, index) }))
      .sort((left, right) => {
        const rank = (o) =>
          o.audioType === 'ORIGINAL' ? 0 : o.outputKind === 'MEETING_MATERIALS' ? 3 : o.audioType === 'SILENT' ? 1 : 2
        return rank(left) - rank(right)
      })
  )

  const playableOutputs = computed(() =>
    outputCards.value.filter((output) => output.status === 'COMPLETED' && Boolean(output.videoUrl))
  )

  const activeOutput = computed(
    () =>
      playableOutputs.value.find((output) => output.key === activeKey.value) ||
      (retainedOutput.value?.key === activeKey.value ? retainedOutput.value : null)
  )

  watch(
    playableOutputs,
    (outputs) => {
      const availableKeys = new Set(outputs.map((output) => output.key))
      readyKeys.forEach((key) => {
        if (!availableKeys.has(key)) readyKeys.delete(key)
      })
      if (!availableKeys.has(lastReadyKey.value)) lastReadyKey.value = ''
      if (!availableKeys.has(fallbackKey.value)) fallbackKey.value = ''

      if (!outputs.length) {
        activeKey.value = ''
        pendingRestore.value = null
        playbackWasActive.value = false
        return
      }
      if (!availableKeys.has(activeKey.value)) {
        // Finish playback of the previously opened file; a rebuilt file is selected explicitly.
        if (
          retainedOutput.value?.key === activeKey.value &&
          outputs.some((output) => output.id === retainedOutput.value.id)
        )
          return
        const original = outputs.find((output) => output.audioType === 'ORIGINAL')
        activeKey.value = (original || outputs[0]).key
        pendingRestore.value = null
      }
      retainedOutput.value = outputs.find((output) => output.key === activeKey.value) || null
    },
    { immediate: true }
  )

  const playerState = (player) => ({
    currentTime: Number.isFinite(player?.currentTime) ? player.currentTime : 0,
    shouldPlay: player ? !player.paused : false,
  })

  const switchOutput = async (output) => {
    if (output.key === activeKey.value || !playableOutputs.value.some((item) => item.key === output.key)) return
    const state = playerState(videoRef.value)
    playbackWasActive.value = state.shouldPlay
    fallbackKey.value =
      lastReadyKey.value && lastReadyKey.value !== output.key && readyKeys.has(lastReadyKey.value)
        ? lastReadyKey.value
        : ''
    pendingRestore.value = { ...state, targetKey: output.key }
    isRecovering.value = false
    retainedOutput.value = { ...output }
    activeKey.value = output.key
    await nextTick()
  }

  const handleLoadedMetadata = async (event) => {
    const player = event.currentTarget
    const state = pendingRestore.value
    if (player !== videoRef.value || !state || state.targetKey !== activeKey.value) return
    try {
      const upperBound = Number.isFinite(player.duration) ? Math.max(0, player.duration - 0.05) : state.currentTime
      player.currentTime = Math.min(Math.max(0, state.currentTime), upperBound)
    } catch (error) {
      // 时长元数据异常时仍允许从头播放。
    }
    pendingRestore.value = null
    if (state.shouldPlay) {
      try {
        await player.play()
      } catch (error) {
        // 浏览器自动播放策略可能阻止恢复播放，时间位置仍会保留。
      }
    }
  }

  const handleCanPlay = (event) => {
    if (event.currentTarget !== videoRef.value || !activeKey.value) return
    readyKeys.add(activeKey.value)
    lastReadyKey.value = activeKey.value
    isRecovering.value = false
  }

  const handlePlay = (event) => {
    if (event.currentTarget === videoRef.value) playbackWasActive.value = true
  }

  const handlePause = (event) => {
    if (event.currentTarget === videoRef.value && !event.currentTarget.error) playbackWasActive.value = false
  }

  const handlePlaybackError = async (event) => {
    if (event.currentTarget !== videoRef.value) return
    const fallback = playableOutputs.value.find(
      (output) => output.key === fallbackKey.value && readyKeys.has(output.key)
    )
    if (isRecovering.value || !fallback || fallback.key === activeKey.value) {
      ElMessage.error('当前成片加载失败，请稍后重试')
      return
    }

    const failedPlayer = event.currentTarget
    const pendingTime = pendingRestore.value?.targetKey === activeKey.value ? pendingRestore.value.currentTime : null
    const currentTime = Number.isFinite(pendingTime)
      ? pendingTime
      : Number.isFinite(failedPlayer.currentTime)
      ? failedPlayer.currentTime
      : 0
    pendingRestore.value = {
      targetKey: fallback.key,
      currentTime,
      shouldPlay: playbackWasActive.value,
    }
    isRecovering.value = true
    fallbackKey.value = ''
    activeKey.value = fallback.key
    ElMessage.warning('当前版本加载失败，已回退到上一个验证可播放的版本')
    await nextTick()
  }
</script>

<template>
  <section class="replay-player">
    <div class="player-heading">
      <div>
        <span class="eyebrow">OUTPUT MONITOR</span>
        <h2>成片监看</h2>
      </div>
      <span class="output-count">{{ playableOutputs.length }} 个可用版本</span>
    </div>

    <div class="screen-shell">
      <video
        v-if="activeOutput"
        :key="activeOutput.key"
        ref="videoRef"
        controls
        playsinline
        preload="metadata"
        :src="activeOutput.videoUrl"
        @canplay="handleCanPlay"
        @error="handlePlaybackError"
        @loadedmetadata="handleLoadedMetadata"
        @pause="handlePause"
        @play="handlePlay"
      />
      <div v-else class="empty-screen">
        <div class="empty-mark">▶</div>
        <strong>暂无可播放成片</strong>
        <span>成片保存完成后即可播放</span>
      </div>
      <div v-if="activeOutput" class="screen-badge">
        <span class="live-dot"></span>
        {{ replayOutputLabel(activeOutput) }}
      </div>
    </div>

    <ReplayQualityNotice :output="activeOutput" />

    <ReplayOutputCards
      :active-key="activeKey"
      :allow-regenerate="allowRegenerate"
      :busy="busy"
      :outputs="outputCards"
      :task-status="task?.status"
      @regenerate="emit('regenerate', $event)"
      @select="switchOutput"
    />
  </section>
</template>

<style lang="scss" scoped>
  .replay-player {
    min-width: 0;
    padding: 24px;
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  }

  .player-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 18px;

    h2 {
      margin: 4px 0 0;
      color: #303133;
      font-size: 20px;
      font-weight: 500;
      letter-spacing: -0.02em;
    }
  }

  .eyebrow {
    color: var(--el-color-primary, #1890ff);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
  }

  .output-count {
    color: #909399;
    font-size: 12px;
  }

  .screen-shell {
    position: relative;
    overflow: hidden;
    aspect-ratio: 16 / 9;
    background: radial-gradient(circle at 45% 40%, #27334a, #080c14 68%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;

    video {
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: #05070b;
    }
  }

  .empty-screen {
    display: flex;
    height: 100%;
    padding: 20px;
    align-items: center;
    justify-content: center;
    color: #dce2ec;
    flex-direction: column;
    text-align: center;
    gap: 10px;

    span:last-child {
      color: #768197;
      font-size: 13px;
    }
  }

  .empty-mark {
    display: grid;
    width: 58px;
    height: 58px;
    padding-left: 4px;
    place-items: center;
    color: #fff;
    background: var(--el-color-primary, #1890ff);
    border-radius: 50%;
  }

  .screen-badge {
    position: absolute;
    top: 16px;
    left: 16px;
    display: flex;
    padding: 7px 10px;
    align-items: center;
    color: #eaf0f7;
    background: rgba(8, 12, 20, 0.72);
    border: 1px solid rgba(255, 255, 255, 0.11);
    border-radius: 999px;
    font-size: 12px;
    backdrop-filter: blur(8px);
    gap: 7px;
  }

  .live-dot {
    width: 6px;
    height: 6px;
    background: var(--el-color-primary, #1890ff);
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(24, 144, 255, 0.8);
  }
</style>
